/**
 * Which groups of a profile the product still needs before it can be used.
 *
 * Deliberately answers with group names and not screen names. `checkSignupCompletion` used to
 * return the route to navigate to, which put a navigation decision inside a data question and
 * meant the flow could only be followed by performing it. Mapping a group to a destination is
 * the bridge's job. See EPIC-005.
 */
/**
 * The groups are a product rule, not an implementation detail.
 *
 * Personal details and work details are asked on two separate screens deliberately, and the
 * split is governed by the Notion "Kite Business Rules" database that `docs/PROJECT.md`
 * cites — not by this file. Merging them changes product behaviour and is not a refactor.
 *
 * Read by the action gate, never by signing in. The product asks for the missing details when
 * an action needs them — reviewing, publishing a story, finishing a lesson — through
 * `ensureProfileCompletion`. Signing in only asks whether a profile exists. See EPIC-005.
 */
export type ProfileGroup = 'signup' | 'personal' | 'work'

type CompletableProfile = {
  first_name?: unknown
  last_name?: unknown
  email?: unknown
  birthday?: unknown
  gender?: unknown
  country?: unknown
  seniority?: unknown
  department?: unknown
  organization?: { id?: unknown } | null
} | null

const present = (value: unknown) => value !== null && value !== undefined

const GROUPS: [ProfileGroup, (profile: CompletableProfile) => unknown[]][] = [
  ['signup', profile => [profile?.first_name, profile?.last_name, profile?.email]],
  ['personal', profile => [profile?.birthday, profile?.gender, profile?.country]],
  ['work', profile => [profile?.seniority, profile?.organization?.id, profile?.department]]
]

export function missingFrom(profile: CompletableProfile): ProfileGroup[] {
  if (!profile) return GROUPS.map(([group]) => group)

  return GROUPS.filter(([, read]) => !read(profile).every(present)).map(([group]) => group)
}
